export const ErrorsAdditionalAccount: Record<string, string> = {
  VAULT_HAS_BALANCE: 'No puedes eliminar esta cuenta porque tiene saldo disponible. Transfiere el saldo primero.',
  ACCOUNT_IS_CLOSED: 'Esta cuenta ya fue eliminada.',
  ACCOUNT_CURRENCY_MISMATCH: 'La cuenta destino debe ser de la misma moneda que la cuenta que estás eliminando.',
  /**
   * Nombre de cuenta duplicado (case-insensitive, entre las cuentas no cerradas
   * de la empresa). UN SOLO reason para los DOS flujos: crear (ML-3) y renombrar
   * (ML-40/ML-50) pasan por el mismo `AccountNameHelper.ensureNameIsFree` de
   * ms-wallet. Verificado contra el master del MS y contra la respuesta real del
   * endpoint (`{code: "027409", reason: "ACCOUNT_ALREADY_EXISTS"}`).
   *
   * El `ACCOUNT_NAME_ALREADY_EXISTS` que estaba acá salió de la descripción de
   * la HU y NO existe en el backend: el error del rename caía al mensaje
   * genérico. No reponerlo sin verlo en una respuesta real.
   *
   * Copy propuesto, confirmar con diseño (el Figma 1606-18220 no define el
   * estado de error).
   */
  ACCOUNT_ALREADY_EXISTS: 'Ya tienes una cuenta con este nombre. Ingresa un nombre diferente.',
  /**
   * ML-3 — cubre TRES condiciones distintas del backend con el mismo reason:
   * empresa sin `countryCode`, sin cuenta principal activa en esa moneda
   * (`NO_MAIN_ACCOUNT_FOR_CURRENCY` solo se loguea, no viaja) y combinación
   * país-moneda sin habilitar en `additional_account_cfg`. De ahí el copy
   * genérico: no hay forma de distinguirlas desde el front.
   */
  CREATE_ACCOUNT_NOT_POSSIBLE: 'No puedes crear una cuenta secundaria en esta moneda. Comunícate con tu ejecutivo.',
  /**
   * ML-3 — límite de cuentas adicionales de la moneda alcanzado. El reason es
   * engañoso a propósito del backend (`// Cambiar error reason` en
   * `AdditionalAccountContextResolver`): si lo renombran, este error cae al
   * default del flujo hasta que se actualice la clave.
   */
  TRX_LIMIT_EXCEEDED: 'Alcanzaste el máximo de cuentas secundarias para esta moneda.',
};

/**
 * ML-66 — `defaultMessage` para el listado de cuentas de un usuario.
 *
 * Va como constante y NO como entrada `GENERIC_ERROR` del mapa: `getReasonMessage`
 * arma `{ ...ErrorsCommon, ...errorMap }` y el hit en el mapa **gana sobre el
 * defaultMessage**, así que una clave genérica acá le pisaría el mensaje a los
 * otros consumidores del mapa — el cierre (`useAdditionalAccounts`) y el
 * renombrado (`useEditAccountDrawer`) mostrarían "no pudimos cargar
 * las cuentas" ante un fallo de red al eliminar o editar.
 */
export const USER_ACCOUNTS_ERROR_MESSAGE =
  'No pudimos cargar las cuentas de este usuario. Vuelve a intentarlo en unos minutos.';

/** Copy verbatim de ML-51 para el error de eliminación no mapeado a un reason conocido. */
export const DELETE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE = 'No pudimos eliminar tu cuenta, por favor intentalo mas tarde.';

/** ML-50 — default del drawer de edición para reasons no mapeados (espejo del de eliminación). */
export const EDIT_ADDITIONAL_ACCOUNT_ERROR_MESSAGE =
  'No pudimos guardar los cambios de tu cuenta, por favor inténtalo más tarde.';

/** ML-3 — default del drawer de creación para reasons no mapeados. */
export const CREATE_ADDITIONAL_ACCOUNT_ERROR_MESSAGE =
  'No pudimos crear tu cuenta secundaria, por favor inténtalo más tarde.';

/**
 * ML-67 — `defaultMessage` del catálogo de cuentas de la empresa.
 *
 * Misma razón que `USER_ACCOUNTS_ERROR_MESSAGE`: constante y no clave del mapa.
 */
export const COMPANY_ACCOUNTS_CATALOG_ERROR_MESSAGE =
  'No pudimos cargar las cuentas de tu empresa. Vuelve a intentarlo en unos minutos.';

/**
 * ML-67 — `defaultMessage` para el revocado de acceso de un usuario a una cuenta.
 *
 * Los reasons que devuelve el endpoint son `ACCOUNT_NOT_FOUND` y `BAD_REQUEST`,
 * y **a propósito no se agregan al mapa**: los dos son reasons que el cierre de
 * cuenta (`closeAdditionalAccount`) también devuelve, y comparten este mapa. Una
 * clave `ACCOUNT_NOT_FOUND` acá le cambiaría el mensaje al flujo de eliminar una
 * cuenta, que está en producción — `getReasonMessage` arma
 * `{ ...ErrorsCommon, ...errorMap }` y el hit en el mapa gana sobre el
 * defaultMessage. Es el mismo choque que ya se documentó en ML-66.
 *
 * El copy es deliberadamente uno solo para los dos reasons: ni "la cuenta no
 * existe" ni "pedido inválido" le dicen nada útil a un admin que solo quiere
 * quitarle un acceso a alguien, y los dos se resuelven reintentando o
 * recargando.
 */
export const UNASSIGN_USER_ACCOUNT_ERROR_MESSAGE =
  'No pudimos quitar el acceso a esta cuenta. Vuelve a intentarlo en unos minutos.';

/**
 * ML-96 — fallo PARCIAL de `applyAccountEdit`: alguna alta o baja del diff de
 * usuarios fue rechazada por el backend, aunque el resto sí se aplicó (el
 * `Promise.all` no es atómico). El drawer de edición usa este mensaje para NO
 * tostear éxito ni cerrarse — ver `useEditAccountDrawer.ts`.
 */
export const EDIT_ADDITIONAL_ACCOUNT_PARTIAL_FAILURE_MESSAGE =
  'No pudimos aplicar todos los cambios de usuarios en esta cuenta. Vuelve a intentarlo en unos minutos.';
